export interface IRemoteFilter {
   property: string
   operator: "=" | "<" | ">" | "<=" | ">=" | "list" | "like"
   value:
      | boolean
      | string
      | number
      | Array<string>
      | Array<number>
      | undefined
      | null
}

export interface IRemoteFilterConfig {
   queryParamName: string
}

/**
 * This class provides convinient way to parse/stringify filters which are used to tell backend how result should be filtered.
 *
 * How to use it:
 * ```ts
   import { RemoteFilters } from "design-system-ui-components"

   const filters = new RemoteFilters()
   filters.add({ property: "name", operator: "=", value: "kowalski" })
   filters.add({ property: "age", operator: "=", value: 20 })
   const urlWithFilters = "http://domain.com/?" + filters.stringify()

   "urlWithFilters" is:
   `http://domain.com/?filters=%5B%7B%22property%22%3A%22name%22%2C%22operator%22%3A%22%3D%22%2C%22value%22%3A%22kowalski%22%7D
   %2C%7B%22property%22%3A%22age%22%2C%22operator%22%3A%22%3D%22%2C%22value%22%3A20%7D%5D`

   We can also parse filters from url (lets assume we have url like above).
   const filters = new RemoteFilters()
   filters.parse()

   Result of "filters.getAll()" is:
   [{
      property: "name",
      operator: "=",
      value: "kowalski"
   }, {
      property: "age",
      operator: "=",
      value: 20
   }]

 * ```
 */
export class RemoteFilters {
   private queryParamName = "filters"
   private items: IRemoteFilter[] = []

   /**
    * Contructor
    * @param config - Config where we can setup e.g. `queryParamName`
    */
   constructor(config?: IRemoteFilterConfig) {
      config?.queryParamName
         ? (this.queryParamName = config.queryParamName)
         : null
   }

   /**
    * Gets the filter object with given property name.
    *
    * @param property - Property name which is used to find filter object
    */
   get(property: IRemoteFilter["property"]): IRemoteFilter | undefined {
      return this.items.find((filter) => {
         return filter.property === property
      })
   }

   /**
    * Returns all filters.
    */
   getAll(): IRemoteFilter[] {
      return this.items
   }

   /**
    * Add filter.
    */
   add(filter: IRemoteFilter) {
      this.items.push(filter)
   }

   /**
    * Remove filter with given property name
    * @param property - Property name which is used to find filter
    */
   remove(property: IRemoteFilter["property"]) {
      this.items = this.items.filter((filter) => {
         return filter.property !== property
      })
   }

   /**
    * Method parse `window.location.search` or provided string.
    * All found filters from uri are added and can be retrived using methods `get` and `getAll`.
    *
    * @param urlSearchParams - String which must be like => filter=encoded(json)
    */
   parse(urlSearchParams?: string) {
      const params = new URLSearchParams(
         urlSearchParams ? urlSearchParams : window.location.search
      )

      if (params.has(this.queryParamName)) {
         try {
            const filters = decodeURIComponent(
               params.get(this.queryParamName) || ""
            )
            this.items = JSON.parse(filters)
         } catch (err) {
            console.error(err)
         }
      }
   }

   /**
    * It stringify all filters as a uriEncoded json.
    */
   stringify() {
      return (
         this.queryParamName +
         "=" +
         encodeURIComponent(JSON.stringify(this.items))
      )
   }
}
